import { blocksClient } from '@/lib/blocks/client';
import {
  DisableUserMfaResponse,
  GenerateOTPPayload,
  GenerateOTPResponse,
  GetMfaTemplateResponse,
  GetSetUpTotpResponse,
  ResendOtpResponse,
  SetMfaMethodResponse,
  VerifyOTP,
  VerifyOTPResponse,
  VerifyTotpSetupResponse,
} from '../types/mfa.types';

/**
 * Self-service MFA for the signed-in user, backed by the Blocks IAM `/iam/v4/mfa/*` endpoints
 * through `blocksClient.mfa`. The legacy `/idp/v1/Mfa/*` routes no longer exist on the Blocks API
 * (they answer 404 `Application_Not_Found`), and IAM identifies the user from the access token,
 * so none of these calls take a `userId` or `projectKey` any more.
 *
 * The SDK passes IAM's responses through untyped, and IAM's casing varies between camelCase and
 * PascalCase, so responses are read through {@link readField} and normalised to the shapes the
 * profile UI already consumes. A failed request rejects with `BlocksApiError`.
 */

type RawResponse = Record<string, unknown> | undefined;

/** Reads a field from an IAM response whichever way IAM cased it (`mfaId` / `MfaId`). */
const readField = <T>(response: RawResponse, name: string): T | undefined => {
  if (!response) return undefined;
  const pascalName = name.charAt(0).toUpperCase() + name.slice(1);
  return (response[name] ?? response[pascalName]) as T | undefined;
};

/** IAM answers 2xx on success; only an explicit `isSuccess: false` marks a soft failure. */
const readIsSuccess = (response: RawResponse): boolean =>
  readField<boolean>(response, 'isSuccess') !== false;

/**
 * Sends a one-time password (email OTP) challenge to the signed-in user.
 *
 * @param {GenerateOTPPayload} payload - The MFA type to send the challenge for.
 * @returns {Promise<GenerateOTPResponse>} The challenge id to pass to `verifyOTP` / `resendOtp`.
 * @throws {BlocksApiError} If the request fails.
 *
 * @example
 * const { mfaId } = await generateOTP({ mfaType: 2 });
 */
export const generateOTP = async ({
  mfaType,
}: GenerateOTPPayload): Promise<GenerateOTPResponse> => {
  const res = await blocksClient.mfa.generate({ mfaType });
  return {
    isSuccess: readIsSuccess(res),
    mfaId: readField<string>(res, 'mfaId') ?? '',
  };
};

/**
 * Verifies the OTP entered by the user against an issued challenge.
 *
 * @param {VerifyOTP} payload - The verification code, the challenge `mfaId` and the `authType`.
 * @returns {Promise<VerifyOTPResponse>} Whether the code was accepted.
 * @throws {BlocksApiError} If the request fails or the code is rejected.
 *
 * @example
 * const { isValid } = await verifyOTP({ verificationCode: '12345', mfaId, authType: 2 });
 */
export const verifyOTP = async (payload: VerifyOTP): Promise<VerifyOTPResponse> => {
  const res = await blocksClient.mfa.verify(payload);
  const isSuccess = readIsSuccess(res);
  return {
    isSuccess,
    isValid: readField<boolean>(res, 'isValid') ?? isSuccess,
  };
};

/**
 * Starts authenticator-app (TOTP) enrollment for the signed-in user.
 *
 * @returns {Promise<GetSetUpTotpResponse>} The QR image and the manual-entry key to show the user.
 * @throws {BlocksApiError} If the request fails.
 */
export const getSetUpTotp = async (): Promise<GetSetUpTotpResponse> => {
  const res = await blocksClient.mfa.totp.setup();
  return {
    isSuccess: readIsSuccess(res),
    qrImageUrl: readField<string>(res, 'qrImageUrl') ?? '',
    qrCode: readField<string>(res, 'qrCode') ?? readField<string>(res, 'secret') ?? '',
  };
};

/**
 * Completes authenticator-app enrollment: confirms the 6-digit code from the app, then makes TOTP
 * the user's active MFA method (the same `totp setup` → `verify-setup` → `method set` sequence the
 * Blocks CLI's `mfa totp enable` runs).
 *
 * @param {string} code - The code shown in the authenticator app.
 * @returns {Promise<VerifyTotpSetupResponse>} Whether the code was accepted.
 * @throws {BlocksApiError} If the request fails or the code is rejected.
 */
export const verifyTotpSetup = async (code: string): Promise<VerifyTotpSetupResponse> => {
  const res = await blocksClient.mfa.totp.verifySetup({ code });
  const isSuccess = readIsSuccess(res);
  const isValid = readField<boolean>(res, 'isValid') ?? isSuccess;
  if (isSuccess && isValid) {
    await blocksClient.mfa.setMethod({ mfaType: 1 });
  }
  return { isSuccess, isValid };
};

/**
 * Makes an already-verified MFA method the user's active one.
 *
 * @param {number} mfaType - IAM `UserMfaType`: 1 for TOTP, 2 for email. Any other value makes IAM
 * turn MFA off, so callers must only pass 1 or 2.
 * @throws {BlocksApiError} If the request fails.
 */
export const setMfaMethod = async (mfaType: 1 | 2): Promise<SetMfaMethodResponse> => {
  const res = await blocksClient.mfa.setMethod({ mfaType });
  return { isSuccess: readIsSuccess(res) };
};

/**
 * Re-sends a pending OTP.
 *
 * @param {string} mfaId - The challenge id returned by `generateOTP`.
 * @returns {Promise<ResendOtpResponse>} The (possibly new) challenge id to verify against.
 * @throws {BlocksApiError} If the request fails.
 */
export const resendOtp = async (mfaId: string): Promise<ResendOtpResponse> => {
  const res = await blocksClient.mfa.resend({ mfaId });
  return {
    isSuccess: readIsSuccess(res),
    mfaId: readField<string>(res, 'mfaId') ?? mfaId,
  };
};

/**
 * Reads the tenant's MFA policy: whether MFA is enabled and which methods users may choose.
 *
 * @returns {Promise<GetMfaTemplateResponse>} The tenant's MFA policy.
 * @throws {BlocksApiError} If the request fails.
 */
export const getMfaTemplate = async (): Promise<GetMfaTemplateResponse> => {
  return blocksClient.mfa.config();
};

/**
 * Disables MFA for the signed-in user (allowed only when the tenant's policy permits opt-out).
 *
 * @returns {Promise<DisableUserMfaResponse>} Whether MFA was disabled.
 * @throws {BlocksApiError} If the request fails or the policy forbids opting out.
 */
export const disableUserMfa = async (): Promise<DisableUserMfaResponse> => {
  const res = await blocksClient.mfa.disable();
  return { isSuccess: readIsSuccess(res) };
};
